import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

function isCartAddForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  if (form.closest('product-form-component')) return false;
  if (form.matches('.java-cart-upsell__form')) return false;

  try {
    const url = new URL(form.action, window.location.origin);
    return /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
  } catch {
    return false;
  }
}

function getSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean)
    .filter((id, index, list) => list.indexOf(id) === index);
}

async function submitWithAjax(form, submitter) {
  const formData = new FormData(form);
  const sectionIds = getSectionIds();

  if (sectionIds.length) {
    formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);
  }

  if (submitter?.name && !formData.has(submitter.name)) {
    formData.append(submitter.name, submitter.value || '');
  }

  const originalHTML = submitter instanceof HTMLButtonElement ? submitter.innerHTML : '';
  if (submitter instanceof HTMLButtonElement) {
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
  }

  try {
    const config = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'application/json' },
    });
    const payload = await response.json();

    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || 'No se pudo añadir el producto.');
    }

    form.dispatchEvent(new CartAddEvent(payload, 'java-global-add-to-cart', {
      source: 'java-global-add-to-cart',
      itemCount: Number(formData.get('quantity')) || 1,
      variantId: String(formData.get('id') || ''),
      sections: payload.sections,
    }));
  } catch (error) {
    console.error('[Java Cart] Add to cart failed:', error);
    // If AJAX fails, preserve Shopify's native behavior as a safe fallback.
    form.submit();
    return;
  } finally {
    if (submitter instanceof HTMLButtonElement && submitter.isConnected) {
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
      if (originalHTML) submitter.innerHTML = originalHTML;
    }
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!isCartAddForm(form)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  submitWithAjax(form, event.submitter);
}, true);


// -----------------------------------------------------------------------------
// Java Times product URL guard
// The /en/products/... route currently returns Shopify's 404 page on this shop,
// while /products/... is the valid canonical product route. Normalize links that
// Shopify/PageFly can generate while browsing under a locale prefix.
// -----------------------------------------------------------------------------
const JAVA_LOCALIZED_PRODUCT_PATH = /^\/[a-z]{2}(?:-[A-Za-z]{2})?\/products\//;

function javaCanonicalProductUrl(rawUrl) {
  if (!rawUrl) return rawUrl;
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.origin !== window.location.origin) return rawUrl;
    if (!JAVA_LOCALIZED_PRODUCT_PATH.test(url.pathname)) return rawUrl;

    url.pathname = url.pathname.replace(/^\/[a-z]{2}(?:-[A-Za-z]{2})?(?=\/products\/)/, '');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return rawUrl;
  }
}


// Redirect a broken localized product URL that was opened directly or from an old link.
const javaCurrentCanonicalProductUrl = javaCanonicalProductUrl(window.location.href);
if (javaCurrentCanonicalProductUrl && JAVA_LOCALIZED_PRODUCT_PATH.test(window.location.pathname)) {
  window.location.replace(javaCurrentCanonicalProductUrl);
}

// Readability layer for the native Java cart. Syne stays as the family, using the
// regular/body cut for dense information and heavier weights only for hierarchy.
if (!document.getElementById('java-cart-readable-style')) {
  const style = document.createElement('style');
  style.id = 'java-cart-readable-style';
  style.textContent = `
    .java-cart-drawer,
    .java-cart-drawer button,
    .java-cart-drawer input,
    .java-cart-drawer select,
    .java-cart-drawer option,
    .java-cart-drawer a {
      font-family: var(--font-body--family), sans-serif !important;
      font-style: normal;
      letter-spacing: 0;
    }
    .java-cart-drawer { font-size: 13px; font-weight: 400; line-height: 1.42; }
    .java-cart-drawer__header .cart-drawer__heading { font-size: 21px; font-weight: 600; line-height: 1.15; letter-spacing: -.015em; }
    .java-cart-shipping__message { font-size: 13px; font-weight: 400; line-height: 1.4; }
    .java-cart-shipping__message strong { font-weight: 600; }
    .java-cart-shipping__label { font-size: 11px; font-weight: 500; }
    .java-cart-drawer .cart-items__details *,
    .java-cart-drawer .cart-items__variants,
    .java-cart-drawer .cart-items__properties,
    .java-cart-drawer .cart-items__bundle { font-size: 12.5px; line-height: 1.42; }
    .java-cart-drawer .cart-items__title { font-size: 15px; font-weight: 600; line-height: 1.28; letter-spacing: -.01em; }
    .java-cart-drawer .cart-items__price { font-size: 14px; font-weight: 600; line-height: 1.3; }
    .java-cart-drawer .compare-at-price { font-size: 11.5px; }
    .java-cart-drawer .quantity-selector,
    .java-cart-drawer .quantity-selector input,
    .java-cart-drawer .quantity-selector button { font-size: 13px; font-weight: 500; }
    .java-cart-upsell__eyebrow { font-size: 10px; font-weight: 600; letter-spacing: .10em; }
    .java-cart-upsell__heading-row h3 { font-size: 18px; font-weight: 600; line-height: 1.16; letter-spacing: -.015em; }
    .java-cart-upsell__heading-row p { font-size: 12px; line-height: 1.4; }
    .java-cart-upsell__next { font-size: 11.5px; font-weight: 600; }
    .java-cart-upsell-card__title { font-size: 14px; font-weight: 600; line-height: 1.3; }
    .java-cart-upsell-card__price { font-size: 13.5px; line-height: 1.3; }
    .java-cart-upsell-card__price strong { font-weight: 600; }
    .java-cart-upsell-card__price s { font-size: 11.5px; }
    .java-cart-upsell-card__select { min-height: 38px; font-size: 12px; line-height: 1.25; }
    .java-cart-upsell-card__button { min-height: 38px; padding-inline: 13px; font-size: 12px; font-weight: 600; line-height: 1.2; }
    .java-cart-trust__item { font-size: 11px; font-weight: 400; line-height: 1.25; }
    .java-cart-total { font-size: 13.5px; line-height: 1.3; }
    .java-cart-total strong { font-weight: 600; }
    .java-cart-checkout { min-height: 50px; font-size: 15px; font-weight: 600; line-height: 1.2; }
    .java-cart-continue { font-size: 11px; font-weight: 500; }
  `;
  document.head.appendChild(style);
}

function javaNormalizeProductLinks(scope = document) {
  const anchors = scope.querySelectorAll?.('a[href]') || [];
  anchors.forEach((anchor) => {
    const fixed = javaCanonicalProductUrl(anchor.getAttribute('href'));
    if (fixed && fixed !== anchor.getAttribute('href')) anchor.setAttribute('href', fixed);
  });

  const dataUrlNodes = scope.querySelectorAll?.('[data-product-url]') || [];
  dataUrlNodes.forEach((node) => {
    const current = node.getAttribute('data-product-url');
    const fixed = javaCanonicalProductUrl(current);
    if (fixed && fixed !== current) node.setAttribute('data-product-url', fixed);
  });

  // Some custom product blocks store routing data as serialized JSON in gp-data.
  const gpNodes = scope.querySelectorAll?.('[gp-data]') || [];
  gpNodes.forEach((node) => {
    const current = node.getAttribute('gp-data');
    if (!current || !/\/[a-z]{2}(?:-[A-Za-z]{2})?\/products\//.test(current)) return;
    const fixed = current.replace(/\/[a-z]{2}(?:-[A-Za-z]{2})?(\/products\/)/g, '$1');
    if (fixed !== current) node.setAttribute('gp-data', fixed);
  });
}

// Fix links already present in the page and links inserted later by sections/apps.
javaNormalizeProductLinks(document);

const javaProductLinkObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) javaNormalizeProductLinks(node);
    });
  });
});

javaProductLinkObserver.observe(document.documentElement, { childList: true, subtree: true });

// Last-resort click guard in case a third-party component creates a bad href
// immediately before navigation.
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const anchor = event.target.closest?.('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  const fixed = javaCanonicalProductUrl(anchor.getAttribute('href'));
  if (!fixed || fixed === anchor.getAttribute('href')) return;
  event.preventDefault();
  window.location.assign(fixed);
}, true);
